import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiErrorDetail, money } from "@/lib/api";
import { eurEquivalent } from "@/lib/currency";
import { useAuth } from "@/context/AuthContext";
import { Navbar, DashHeader, StatusBadge, PaymentBadge, Stars, Avatar, NotificationToggle } from "@/components/shared";
import { groupCategories } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, CreditCard, Star, ShieldCheck, Briefcase, Inbox, Check, Navigation, Loader2, MapPinned } from "lucide-react";

export default function ClientDashboard() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [bookings, setBookings] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [groups, setGroups] = useState([]);
    const [offersMap, setOffersMap] = useState({});

    const load = useCallback(async () => {
        const [b, j, c, g] = await Promise.all([
            api.get("/bookings"),
            api.get("/jobs", { params: { mine: true } }),
            api.get("/categories"),
            api.get("/category-groups"),
        ]);
        setBookings(b.data);
        setJobs(j.data);
        setCategories(c.data);
        setGroups(g.data);
    }, []);

    useEffect(() => { load(); }, [load]);

    const loadOffers = async (jobId) => {
        const { data } = await api.get(`/jobs/${jobId}/offers`);
        setOffersMap((m) => ({ ...m, [jobId]: data }));
    };

    const startCheckout = async (id, phase) => {
        try {
            const { data } = await api.post(`/bookings/${id}/checkout`, { phase, origin_url: window.location.origin });
            window.location.href = data.checkout_url;
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const acceptOffer = async (offerId, jobId) => {
        try {
            await api.post(`/offers/${offerId}/accept`);
            toast.success(t("Offerta accettata! Prenotazione creata."));
            await load();
            await loadOffers(jobId);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div className="min-h-screen bg-cream">
            <Navbar />
            <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
                <DashHeader
                    title={t("Ciao, {{name}}", { name: user?.name?.split(" ")[0] })}
                    subtitle={t("Gestisci le tue prenotazioni e richieste.")}
                    action={
                        <div className="flex flex-wrap items-center gap-2">
                            <NotificationToggle />
                            <Button onClick={() => navigate("/professionisti")} className="rounded-full bg-terracotta hover:bg-terracottahover" data-testid="find-pro-btn">
                                {t("Trova un professionista")}
                            </Button>
                        </div>
                    }
                />

                <Tabs defaultValue="bookings">
                    <TabsList className="bg-creamalt w-full grid grid-cols-2">
                        <TabsTrigger value="bookings" data-testid="tab-bookings">{t("Prenotazioni")}</TabsTrigger>
                        <TabsTrigger value="jobs" data-testid="tab-jobs">{t("Le mie richieste")}</TabsTrigger>
                    </TabsList>

                    {/* BOOKINGS */}
                    <TabsContent value="bookings" className="mt-6">
                        {bookings.length === 0 ? (
                            <Empty icon={Inbox} text={t("Nessuna prenotazione. Trova un professionista per iniziare.")} />
                        ) : (
                            <div className="grid gap-4">
                                {bookings.map((b) => (
                                    <div key={b.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`booking-${b.id}`}>
                                        <div className="flex flex-wrap items-start justify-between gap-4">
                                            <div className="flex items-center gap-4">
                                                <Avatar src={b.professional?.photo_url} name={b.professional?.name} className="h-14 w-14 rounded-xl" />
                                                <div>
                                                    <p className="font-heading font-bold text-ink">{b.professional?.name}</p>
                                                    <p className="text-sm text-terracotta">{b.category_name}</p>
                                                    <p className="text-sm text-ink/60 mt-1">{b.description}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-heading text-xl font-bold text-ink">{money(b.price, b.professional?.currency_symbol)}</p>
                                                {eurEquivalent(b.price, b.professional?.currency_code) != null && (
                                                    <p className="text-[11px] text-ink/50">≈ €{eurEquivalent(b.price, b.professional?.currency_code)}</p>
                                                )}
                                                {(b.payment_status === "unpaid" || b.payment_status === "deposit_paid") && b.pricing && (
                                                    <p className="text-xs text-ink/70" data-testid={`breakdown-${b.id}`}>{t("Acconto {{pct}}%: {{deposit}} · Saldo: {{balance}}", { pct: Math.round((b.pricing.deposit_rate || 0.1) * 100), deposit: money(b.pricing.deposit, b.professional?.currency_symbol), balance: money(b.pricing.balance, b.professional?.currency_symbol) })}</p>
                                                )}
                                                <div className="mt-2 flex gap-2 justify-end"><StatusBadge status={b.status} /><PaymentBadge status={b.payment_status} /></div>
                                            </div>
                                        </div>
                                        {(b.payment_status === "unpaid" || b.payment_status === "deposit_paid" || (b.status === "completed" && !b.has_review) || b.has_review) && (
                                        <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-2 border-t border-border pt-4">
                                            {b.payment_status === "unpaid" && (
                                                <Button onClick={() => startCheckout(b.id, "deposit")} size="sm" className="w-full sm:w-auto rounded-full bg-terracotta hover:bg-terracottahover" data-testid={`pay-deposit-${b.id}`}>
                                                    <CreditCard className="h-4 w-4 mr-1" /> {t("Paga acconto")} {money(b.pricing?.deposit, b.professional?.currency_symbol)}
                                                </Button>
                                            )}
                                            {b.payment_status === "deposit_paid" && (
                                                <Button onClick={() => startCheckout(b.id, "balance")} size="sm" className="w-full sm:w-auto rounded-full bg-ink hover:bg-inkhover" data-testid={`pay-balance-${b.id}`}>
                                                    <CreditCard className="h-4 w-4 mr-1" /> {t("Paga saldo")} {money(b.pricing?.balance, b.professional?.currency_symbol)}
                                                </Button>
                                            )}
                                            {b.status === "completed" && !b.has_review && <ReviewDialog booking={b} onDone={load} />}
                                            {b.has_review && <span className="inline-flex items-center gap-1 text-sm text-emerald-700"><Check className="h-4 w-4" /> {t("Recensito")}</span>}
                                        </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* JOBS */}
                    <TabsContent value="jobs" className="mt-6">
                        <div className="mb-5"><NewJobDialog categories={categories} groups={groups} onDone={load} /></div>
                        {jobs.length === 0 ? (
                            <Empty icon={Briefcase} text={t("Nessuna richiesta pubblicata. Pubblicane una e ricevi offerte!")} />
                        ) : (
                            <div className="grid gap-4">
                                {jobs.map((j) => (
                                    <div key={j.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`job-${j.id}`}>
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="font-heading font-bold text-ink">{j.title}</p>
                                                    <StatusBadge status={j.status} />
                                                </div>
                                                <p className="text-sm text-terracotta">{j.category_name} · {j.city}</p>
                                                <p className="text-sm text-ink/60 mt-1">{j.description}</p>
                                            </div>
                                            {j.budget && <p className="font-heading font-bold text-ink shrink-0">€{j.budget}</p>}
                                        </div>
                                        <div className="mt-4 border-t border-border pt-4">
                                            <Button variant="outline" size="sm" className="rounded-full" onClick={() => loadOffers(j.id)} data-testid={`view-offers-${j.id}`}>
                                                <Inbox className="h-4 w-4 mr-1" /> {t("{{count}} offerte", { count: j.offers_count })}
                                            </Button>
                                            {offersMap[j.id]?.length > 0 && (
                                                <div className="mt-3 space-y-2">
                                                    {offersMap[j.id].map((o) => (
                                                        <div key={o.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl bg-creamalt p-3" data-testid={`offer-${o.id}`}>
                                                            <div className="flex items-center gap-3">
                                                                <Avatar src={o.professional?.photo_url} name={o.professional?.name} className="h-9 w-9 rounded-lg" />
                                                                <div>
                                                                    <p className="text-sm font-semibold text-ink">{o.professional?.name}</p>
                                                                    <p className="text-xs text-ink/60">{o.message}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center justify-between gap-3 sm:justify-end">
                                                                <span className="text-left sm:text-right">
                                                                    <span className="font-heading font-bold text-ink block">{money(o.price, o.professional?.currency_symbol)}</span>
                                                                    {eurEquivalent(o.price, o.professional?.currency_code) != null && (
                                                                        <span className="text-[10px] text-ink/50">≈ €{eurEquivalent(o.price, o.professional?.currency_code)}</span>
                                                                    )}
                                                                </span>
                                                                {o.status === "pending" && j.status === "open" ? (
                                                                    <Button size="sm" className="rounded-full bg-terracotta hover:bg-terracottahover" onClick={() => acceptOffer(o.id, j.id)} data-testid={`accept-offer-${o.id}`}>{t("Accetta")}</Button>
                                                                ) : (
                                                                    <StatusBadge status={o.status === "accepted" ? "confirmed" : "cancelled"} />
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {offersMap[j.id]?.length === 0 && <p className="mt-2 text-sm text-ink/50">{t("Ancora nessuna offerta.")}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function Empty({ icon: Icon, text }) {
    return (
        <div className="rounded-2xl border border-dashed border-border bg-white py-16 text-center text-ink/50">
            <Icon className="h-10 w-10 mx-auto mb-3" />
            <p>{text}</p>
        </div>
    );
}

function NewJobDialog({ categories, groups, onDone }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [f, setF] = useState({ title: "", description: "", category: "", city: "", budget: "", lat: null, lng: null });
    const [loading, setLoading] = useState(false);
    const [geoLoading, setGeoLoading] = useState(false);
    const useMyLocation = () => {
        if (!navigator.geolocation) return toast.error(t("Geolocalizzazione non supportata"));
        setGeoLoading(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => { setF((prev) => ({ ...prev, lat: pos.coords.latitude, lng: pos.coords.longitude })); setGeoLoading(false); toast.success(t("Posizione acquisita")); },
            () => { setGeoLoading(false); toast.error(t("Impossibile ottenere la posizione")); },
            { enableHighAccuracy: true, timeout: 8000 }
        );
    };
    const submit = async () => {
        setLoading(true);
        try {
            await api.post("/jobs", { ...f, budget: f.budget ? parseFloat(f.budget) : null });
            toast.success(t("Richiesta pubblicata!"));
            setOpen(false);
            setF({ title: "", description: "", category: "", city: "", budget: "", lat: null, lng: null });
            onDone();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="rounded-full bg-ink hover:bg-inkhover" data-testid="new-job-btn"><Plus className="h-4 w-4 mr-1" /> {t("Pubblica una richiesta")}</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">{t("Nuova richiesta")}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div><Label>{t("Titolo")}</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1.5" data-testid="job-title" placeholder={t("Es. Tinteggiare soggiorno 20mq")} /></div>
                    <div><Label>{t("Descrizione")}</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1.5" data-testid="job-desc" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>{t("Categoria")}</Label>
                            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                                <SelectTrigger className="mt-1.5" data-testid="job-category"><SelectValue placeholder={t("Scegli")} /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {groupCategories(categories, groups).map((g) => (
                                        <SelectGroup key={g.slug}>
                                            <SelectLabel>{g.name}</SelectLabel>
                                            {g.items.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                                        </SelectGroup>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div><Label>{t("Città")}</Label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="mt-1.5" data-testid="job-city" /></div>
                    </div>
                    <div><Label>{t("Budget indicativo (€)")}</Label><Input type="number" value={f.budget} onChange={(e) => setF({ ...f, budget: e.target.value })} className="mt-1.5" data-testid="job-budget" /></div>
                    <div className="rounded-xl border border-border bg-creamalt/50 p-3">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-sm text-ink">
                                <MapPinned className={`h-4 w-4 ${f.lat ? "text-emerald-600" : "text-ink/50"}`} />
                                {f.lat ? t("Posizione precisa acquisita") : t("Aiuta a trovare artigiani vicini")}
                            </div>
                            <Button type="button" variant="outline" size="sm" onClick={useMyLocation} className="rounded-full border-ink/20" data-testid="job-use-location">
                                {geoLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Navigation className="h-3.5 w-3.5 mr-1" />}
                                {f.lat ? t("Aggiorna") : t("Usa la mia posizione")}
                            </Button>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button onClick={submit} disabled={loading || !f.title || !f.category || !f.city} className="w-full rounded-full bg-terracotta hover:bg-terracottahover" data-testid="submit-job">
                        {loading ? t("Pubblico…") : t("Pubblica richiesta")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ReviewDialog({ booking, onDone }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState("");
    const [loading, setLoading] = useState(false);
    const submit = async () => {
        setLoading(true);
        try {
            await api.post("/reviews", { booking_id: booking.id, rating, comment });
            toast.success(t("Grazie per la recensione!"));
            setOpen(false);
            onDone();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="w-full sm:w-auto rounded-full" data-testid={`review-btn-${booking.id}`}><Star className="h-4 w-4 mr-1" /> {t("Lascia recensione")}</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">{t("Recensisci {{name}}", { name: booking.professional?.name })}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
                                <Star className={`h-8 w-8 ${n <= rating ? "fill-terracotta text-terracotta" : "text-ink/20"}`} />
                            </button>
                        ))}
                    </div>
                    <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("Com'è andata?")} data-testid="review-comment" />
                </div>
                <DialogFooter>
                    <Button onClick={submit} disabled={loading || !comment} className="w-full rounded-full bg-terracotta hover:bg-terracottahover" data-testid="submit-review">{t("Invia recensione")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
